'use client';

import { useState, useMemo } from 'react';
import { FormSheetLayout } from '@/components/form-sheet-layout';
import { Input } from '@/components/ui/input';
import { Search, Plus, User, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ClientWithId } from '@/shared/schema';
import { CreateClientModal } from '@/routes/app/global-modals/CreateClientModal';

interface ClientSelectionSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  clients: ClientWithId[];
  onSelect: (client: ClientWithId) => void;
  selectedClientId?: string;
  title?: string;
}

export function ClientSelectionSheet({
  isOpen,
  onOpenChange,
  clients,
  onSelect,
  selectedClientId,
  title = "Mis Clientes",
}: ClientSelectionSheetProps) {
  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const filteredAndSorted = useMemo(() => {
    return clients
      .filter((c) =>
        (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.phone || '').includes(search)
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [clients, search]);

  const handleSelect = (client: ClientWithId) => {
    onSelect(client);
    onOpenChange(false);
  };

  const handleCreateSuccess = (client: ClientWithId) => {
    onSelect(client);
    onOpenChange(false);
    setIsCreateModalOpen(false);
  };

  return (
    <>
      <FormSheetLayout
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        heroTitle={title}
        heroAction={
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="h-10 w-10 bg-zinc-900 text-white rounded-xl flex items-center justify-center shadow-lg active:scale-90 transition-all"
          >
            <Plus className="h-5 w-5" strokeWidth={3} />
          </button>
        }
        primaryAction={{
          label: "Cerrar",
          onClick: () => onOpenChange(false),
        }}
      >
        <div className="space-y-6">
          {/* SEARCH BOX */}
          <div className="relative group">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-300 group-focus-within:text-zinc-900 transition-colors" />
            <Input
              placeholder="BUSCAR CLIENTE..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-16 bg-zinc-50 border-2 border-zinc-100 rounded-2xl pl-14 text-sm font-black uppercase tracking-widest shadow-inner focus-visible:ring-zinc-900"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute right-5 top-1/2 -translate-y-1/2 p-1 bg-zinc-200 rounded-full active:scale-90"
              >
                <X className="h-3 w-3 text-zinc-500" />
              </button>
            )}
          </div>

          {/* LIST */}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
            {filteredAndSorted.map((client) => {
              const isSelected = client.id === selectedClientId;
              return (
                <button
                  key={client.id}
                  onClick={() => handleSelect(client)}
                  className={cn(
                    "w-full flex items-center justify-between p-5 rounded-[2rem] border-2 transition-all active:scale-[0.98]",
                    isSelected 
                      ? "bg-zinc-900 border-zinc-900 text-white shadow-xl" 
                      : "bg-white border-zinc-100 text-zinc-900 shadow-sm"
                  )}
                >
                  <div className="flex items-center gap-4 text-left">
                    <div className={cn(
                      "h-12 w-12 rounded-full flex items-center justify-center text-xs font-black italic",
                      isSelected ? "bg-white/10 text-white" : "bg-zinc-900 text-white"
                    )}>
                      {client.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-black italic uppercase tracking-tighter leading-none">
                        {client.name}
                      </span>
                      {client.phone && (
                        <span className={cn(
                          "text-[10px] font-bold mt-1.5",
                          isSelected ? "text-zinc-400" : "text-zinc-400"
                        )}>
                          {client.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && <Check className="h-5 w-5 text-white" strokeWidth={4} />}
                </button>
              );
            })}

            {/* DYNAMIC CREATE BUTTON */}
            {search && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="w-full flex items-center gap-4 p-5 rounded-[2rem] border-2 border-dashed border-emerald-200 bg-emerald-50 text-emerald-700 transition-all active:scale-[0.98]"
              >
                <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                  <Plus className="h-6 w-6" strokeWidth={3} />
                </div>
                <div className="text-left">
                  <p className="text-[10px] font-black uppercase tracking-widest leading-none opacity-60">Nuevo Registro</p>
                  <p className="text-sm font-black italic uppercase tracking-tighter mt-1">Crear "{search}"</p>
                </div>
              </button>
            )}

            {filteredAndSorted.length === 0 && !search && (
              <div className="py-12 text-center flex flex-col items-center gap-4">
                <div className="h-16 w-16 bg-zinc-50 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-zinc-200" />
                </div>
                <p className="text-[10px] font-black text-zinc-300 uppercase tracking-widest italic">
                  Tu lista está vacía
                </p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="h-12 px-6 rounded-xl bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest shadow-lg"
                >
                  Registrar primer cliente
                </button>
              </div>
            )}
          </div>
        </div>
      </FormSheetLayout>

      <CreateClientModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={handleCreateSuccess}
        defaultValues={{ name: search }}
      />
    </>
  );
}
